import { useState } from 'react';
import Modal from '../../components/Modal';
import IssuerSignerPicker from '../../components/IssuerSignerPicker';
import type { IssuerSigner } from '../../lib/credentialSigning';

interface Props {
  open: boolean;
  title: string;
  /** The DID that signs: the customer. */
  did: string;
  children: React.ReactNode;
  action: string;
  onClose: () => void;
  /** Runs with the chosen signer; a thrown error is shown. */
  onSign: (signer: IssuerSigner) => Promise<void>;
}

/** Shows what the customer is about to sign, then signs it with their key. */
export default function SignWithDialog({ open, title, did, children, action, onClose, onSign }: Props) {
  const [signer, setSigner] = useState<IssuerSigner | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!signer) return;
    setBusy(true);
    setError('');
    try {
      await onSign(signer);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={() => { if (!busy) onClose(); }} title={title} wide>
      <div className="space-y-4 text-sm">
        {children}
        <IssuerSignerPicker did={did} onChange={setSigner} />
        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} disabled={busy} className="rounded-lg border border-border-subtle px-4 py-2 font-medium text-text-main hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={() => void submit()} disabled={busy || !signer} className="btn-primary disabled:opacity-50">
            {busy ? 'Signing…' : action}
          </button>
        </div>
      </div>
    </Modal>
  );
}
